import AppShell from "@/components/AppShell";
import DeleteConfirm from "@/components/DeleteConfirm";
import DetailsPanel from "@/components/DetailsPanel";
import { buildListOptions } from "@/lib/listOptions";
import EmptyState from "@/components/EmptyState";
import { isOfflineStandIn, OFFLINE_EMPTY_TEXT } from "@/lib/swClient";
import Hamburger from "@/components/Hamburger";
import SearchLink from "@/components/SearchLink";
import LotusIcon from "@/components/LotusIcon";
import NewTaskCard from "@/components/NewTaskCard";
import AiActionMenu, { type AiActionEntry } from "@/components/AiActionMenu";
import TaskContextMenu, { type MenuPatch } from "@/components/TaskContextMenu";
import TaskRow, { LEAVING_ROW_CLASSES, type TaskData } from "@/components/TaskRow";
import { classifyReorder, neighborsForReorder, promotedSubtask } from "@/lib/reorder";
import type { BucketKind, SharedProps } from "@/types";
import {
  createTask,
  aiRun,
  aiPreview,
  aiApply,
  toggleTask as mutateToggle,
  deleteTask as mutateDelete,
  updateTask,
  reorderTask,
  bulkPatchTasks,
  bulkDeleteTasks,
  type PreviewOp,
  type AiAnchor,
} from "@/mutations/tasks";
import { useTaskSelection } from "@/hooks/useTaskSelection";
import { useLeavingRows } from "@/hooks/useLeavingRows";
import { taskCount } from "@/lib/plural";
import { useDndMonitor, type DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Link, usePage } from "@inertiajs/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCompletedSection, useLoadedCompleted } from "@/hooks/useCompletedSection";
import { useNow } from "@/hooks/useTimeGate";
import { partitionByVisibility } from "@/lib/visibleFrom";
import { useSelectedRootTask } from "@/hooks/useSelectedRootTask";
import { useNewTaskFocus } from "@/stores/newTaskFocus";
import type { PressPoint } from "@/hooks/useLongPress";
import { useLocalView } from "@/hooks/useLocalView";
import { flushOutbox } from "@/lib/syncEngine";
import { byCompletedAtDesc, listRows, subtasks, type ViewSource } from "@/lib/views";

interface ListInfo {
  id: string;
  name: string;
  kind: BucketKind;
  position: string;
}

interface Props {
  list?: ListInfo;
  pendingTasks: TaskData[];
  completedTasksExist: boolean;
  completedTasks?: TaskData[] | null;
  fetchedAt?: string;
}

const TASKS_ICON = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  </svg>
);

function emptyText(kind: BucketKind, allDone: boolean): string {
  if (kind === "later") {
    return allDone ? "«Задачи» разобраны до дна 🎉 Можно выдохнуть." : "В «Задачах» пусто — ничего не отложено 🎉";
  }
  return allDone ? "Всё закрыто ✨ Отличная работа." : "Пусто. Красиво! ✨ Добавь первую задачу выше.";
}

interface LocalListRows {
  pending: TaskData[];
  completed: TaskData[];
}

const LIST_URL = /^\/lists\/([^/?#]+)/;

function listIdFromUrl(url: string): string | null {
  return LIST_URL.exec(url)?.[1] ?? null;
}

// The device holds what was finished lately; rows the server sent on request add only
// those the device does not know at all, so a row it does know is never shown twice or stale.
function withOlderCompleted(source: ViewSource, recent: TaskData[], fromServer: TaskData[] | null | undefined): TaskData[] {
  if (!fromServer?.length) return recent;
  const known = new Set(source.tasks.map((t) => t.id));
  const older = fromServer.filter((t) => !known.has(t.id));
  return older.length ? [...recent, ...older].sort(byCompletedAtDesc) : recent;
}

export default function ListsShow(props: Props) {
  const url = usePage<SharedProps>().url;
  const listId = props.list?.id ?? listIdFromUrl(url);
  const selectList = useCallback(
    (source: ViewSource): ListInfo | null => {
      const found = source.lists.find((l) => l.id === listId);
      return found ? { id: found.id, name: found.name, kind: found.kind, position: found.position } : null;
    },
    [listId],
  );
  const localList = useLocalView("lists", selectList);
  const list = props.list ?? localList;
  if (!list) {
    return (
      <AppShell>
        {isOfflineStandIn(props.fetchedAt) && (
          <div className="px-5 md:px-7">
            <EmptyState icon={TASKS_ICON} text={OFFLINE_EMPTY_TEXT} />
          </div>
        )}
      </AppShell>
    );
  }
  return <ListPage {...props} list={list} />;
}

function ListPage(props: Props & { list: ListInfo }) {
  const { list } = props;
  const loadedCompleted = useLoadedCompleted(props.completedTasks);
  const selectRows = useCallback(
    (source: ViewSource): LocalListRows => {
      const rows = listRows(source, list.id) as LocalListRows;
      return { pending: rows.pending, completed: withOlderCompleted(source, rows.completed, loadedCompleted) };
    },
    [list.id, loadedCompleted],
  );
  const local = useLocalView("lists", selectRows);
  const pendingTasks = local?.pending ?? props.pendingTasks;
  const completedTasksExist = local ? local.completed.length > 0 || !!props.completedTasksExist : props.completedTasksExist;
  const [taskPendingDelete, setTaskPendingDelete] = useState<TaskData | null>(null);
  const completed = useCompletedSection(loadedCompleted, local?.completed ?? null);
  const [newTitle, setNewTitle] = useState("");
  const [newDueOn, setNewDueOn] = useState<string | null>(null);
  const [newRecurrence, setNewRecurrence] = useState<string | null>(null);
  // A dragged order holds only over the rows it was made from: once they change, they win,
  // in the same render, so a completed row is never shown from a stale copy.
  const [draggedOrder, setDraggedOrder] = useState<{ from: TaskData[]; order: TaskData[] } | null>(null);
  const orderedPending = draggedOrder?.from === pendingTasks ? draggedOrder.order : pendingTasks;
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Subtrees fetched from the server while the local copy is not ready yet.
  const [childrenByParent, setChildrenByParent] = useState<Record<string, TaskData[]>>({});
  const expandedParentIds = useMemo(() => [...expandedIds], [expandedIds]);
  const selectChildren = useCallback((source: ViewSource) => subtasks(source, expandedParentIds), [expandedParentIds]);
  const localChildren = useLocalView("lists", selectChildren) as Record<string, TaskData[]> | null;
  const children = useMemo(
    () => localChildren ?? childrenByParent,
    [localChildren, childrenByParent],
  );
  const [contextMenu, setContextMenu] = useState<{ tasks: TaskData[]; x: number; y: number } | null>(null);
  const [bulkDeletePending, setBulkDeletePending] = useState<TaskData[] | null>(null);

  function patchTaskFromMenu(task: TaskData, payload: MenuPatch) {
    updateTask(task.id, payload, { onSuccess: refreshExpandedSubtrees });
  }
  const sidebar = usePage<SharedProps>().props.sidebar;
  const listOptions = buildListOptions(sidebar);
  const collectionActions = (usePage<SharedProps>().props.aiActions ?? []).filter(
    (a) => a.scope === "collection",
  );

  const aiAnchor: AiAnchor = { kind: "list", id: list.id };
  const collectionEntries: AiActionEntry[] = collectionActions.map((a) => ({
    id: a.id,
    name: a.name,
    confirm: a.confirm,
    run: () => aiRun(aiAnchor, a.id),
    preview: () => aiPreview(aiAnchor, a.id),
    apply: (ops: PreviewOp[]) => aiApply(aiAnchor, ops),
  }));

  const { selectedTask, selectTask } = useSelectedRootTask(pendingTasks, completed.cached);
  const newTaskInputRef = useRef<HTMLInputElement>(null);
  const newTaskFocusRequested = useNewTaskFocus((s) => s.listId === list.id);

  // A subtask dropped on a list becomes a root there (Sidebar fires the request). It has
  // to leave the parent's row here and now: subtasks live in this component's own cache,
  // which the local copy does not reach before it is ready.
  function dropFromSubtree({ taskId, parentId }: { taskId: string; parentId: string }) {
    if (localChildren) return;
    setChildrenByParent((prev) => {
      const siblings = prev[parentId];
      if (!siblings) return prev;
      return { ...prev, [parentId]: siblings.filter((c) => c.id !== taskId) };
    });
  }

  useDndMonitor({
    onDragEnd: (e: DragEndEvent) => {
      const { active, over } = e;
      const activeData = active.data.current as
        | { kind?: string; taskId?: string; parentId?: string | null }
        | undefined;
      const overData = over?.data.current as
        | { kind?: string; parentId?: string | null }
        | undefined;

      const promoted = promotedSubtask(activeData, overData);
      if (promoted) {
        dropFromSubtree(promoted);
        return;
      }

      if (activeData?.kind !== "task" || overData?.kind !== "task") return;
      if (!over || active.id === over.id) return;

      const target = classifyReorder(
        activeData.parentId ?? null,
        overData.parentId ?? null,
      );
      if (target.kind === "noop") return;

      if (target.kind === "root") {
        const oldIndex = orderedPending.findIndex((t) => t.id === active.id);
        const newIndex = orderedPending.findIndex((t) => t.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        const reordered = arrayMove(orderedPending, oldIndex, newIndex);
        setDraggedOrder({ from: pendingTasks, order: reordered });
        const neighbors = neighborsForReorder(reordered, String(active.id));
        reorderTask(String(active.id), neighbors, {
          onError: () => setDraggedOrder(null),
        });
        return;
      }

      const parentId = target.parentId;
      const group = children[parentId] ?? [];
      const oldIndex = group.findIndex((t) => t.id === active.id);
      const newIndex = group.findIndex((t) => t.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(group, oldIndex, newIndex);
      const neighbors = neighborsForReorder(reordered, String(active.id));
      if (localChildren) {
        reorderTask(String(active.id), neighbors);
        return;
      }
      setChildrenByParent((prev) => ({ ...prev, [parentId]: reordered }));
      reorderTask(String(active.id), neighbors, {
        onError: () => setChildrenByParent((prev) => ({ ...prev, [parentId]: group })),
      });
    },
  });

  useEffect(() => {
    if (!newTaskFocusRequested) return;
    newTaskInputRef.current?.focus();
    useNewTaskFocus.getState().clear();
  }, [newTaskFocusRequested]);

  function submitNewTask(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = newTitle.trim();
    if (!trimmed) return;

    createTask({
      title: trimmed,
      listId: list.id,
      dueOn: newDueOn ?? null,
      recurrenceRule: newRecurrence ?? null,
    });

    setNewTitle("");
    setNewDueOn(null);
    setNewRecurrence(null);
  }

  const fetchingRef = useRef<Set<string>>(new Set());

  async function fetchChildren(parentId: string) {
    if (fetchingRef.current.has(parentId)) return;
    fetchingRef.current.add(parentId);
    try {
      await flushOutbox();
      const res = await fetch(`/tasks/${parentId}`, { headers: { Accept: "application/json" } });
      if (!res.ok) return;
      const data = await res.json();
      setChildrenByParent((prev) => ({ ...prev, [parentId]: data.subtasks ?? [] }));
    } finally {
      fetchingRef.current.delete(parentId);
    }
  }

  function refreshExpandedSubtrees() {
    if (localChildren) return Promise.resolve();
    return Promise.all([...expandedIds].map(fetchChildren));
  }

  function toggleExpand(task: TaskData) {
    const id = task.id;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        if (!localChildren && !childrenByParent[id]) void fetchChildren(id);
      }
      return next;
    });
  }

  function toggleTask(task: TaskData) {
    mutateToggle(task, { onSuccess: refreshExpandedSubtrees });
  }

  function renderTaskTree(task: TaskData, depth: number, dndEnabled: boolean): React.ReactNode {
    const isExpanded = expandedIds.has(task.id);
    const shownChildren = visibleChildren(task.id);
    return (
      <div key={task.id} className={task.leaving ? LEAVING_ROW_CLASSES : undefined}>
        <TaskRow
          task={task}
          onToggle={toggleTask}
          onRequestDelete={setTaskPendingDelete}
          onDelete={deleteTaskNow}
          onSelect={selectRow}
          onToggleExpand={toggleExpand}
          onContextMenu={openContextMenu}
          expanded={isExpanded}
          depth={depth}
          draggable={dndEnabled && task.status === "pending" && !task.leaving}
          currentListId={list.id}
          selected={selection.isSelected(task.id)}
          dragGroupIds={selection.dragGroupFor(task.id)}
        />
        {isExpanded && shownChildren.length > 0 && (
          <SortableContext
            items={shownChildren.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            <div>
              {shownChildren.map((child) => renderTaskTree(child, depth + 1, dndEnabled))}
            </div>
          </SortableContext>
        )}
      </div>
    );
  }

  function deleteTaskNow(task: TaskData) {
    const deletedId = task.id;
    const parentId = task.parentId ?? null;
    mutateDelete(deletedId);
    setExpandedIds((prev) => {
      if (!prev.has(deletedId)) return prev;
      const next = new Set(prev);
      next.delete(deletedId);
      return next;
    });
    setChildrenByParent((prev) => {
      const { [deletedId]: _drop, ...rest } = prev;
      if (parentId && rest[parentId]) {
        rest[parentId] = rest[parentId].filter((c) => c.id !== deletedId);
      }
      return rest;
    });
  }

  function confirmDeleteTask() {
    if (!taskPendingDelete) return;
    deleteTaskNow(taskPendingDelete);
    setTaskPendingDelete(null);
  }

  const visibleChildren = useCallback((parentId: string): TaskData[] => children[parentId] ?? [], [children]);

  const listedPending = useLeavingRows(orderedPending);
  const now = useNow();
  const visiblePending = useMemo(
    () => (list.kind === "inbox" ? partitionByVisibility(listedPending, now).shown : listedPending),
    [list.kind, listedPending, now],
  );
  // The active part standing empty always earns an empty state, even with a Completed
  // block below it — the block is then pushed to the bottom so the two don't compete.
  const allDone = visiblePending.length === 0 && completedTasksExist;

  // Flat top-to-bottom order of rendered rows (roots + expanded children) — the axis
  // shift-range selection walks — plus an id→task lookup for the context menu.
  const { visibleOrder, taskById } = useMemo(() => {
    const order: string[] = [];
    const byId = new Map<string, TaskData>();
    const walk = (t: TaskData) => {
      order.push(t.id);
      byId.set(t.id, t);
      if (expandedIds.has(t.id)) {
        for (const c of visibleChildren(t.id)) walk(c);
      }
    };
    visiblePending.forEach(walk);
    return { visibleOrder: order, taskById: byId };
  }, [visiblePending, expandedIds, visibleChildren]);

  const selection = useTaskSelection(visibleOrder);

  function openContextMenu(point: PressPoint, task: TaskData) {
    const ids = selection.selectForContext(task.id);
    const picked = ids.map((id) => taskById.get(id)).filter((t): t is TaskData => !!t);
    setContextMenu({ tasks: picked.length ? picked : [task], x: point.clientX, y: point.clientY });
  }

  function selectRow(task: TaskData, e: React.MouseEvent) {
    const intent = selection.handleClick(task.id, e);
    selectTask(intent === "open" ? task : null);
  }

  function applyMenuPatch(payload: MenuPatch) {
    if (!contextMenu) return;
    const ids = contextMenu.tasks.map((t) => t.id);
    if (ids.length === 1) {
      patchTaskFromMenu(contextMenu.tasks[0], payload);
    } else {
      bulkPatchTasks(ids, payload, { onSuccess: refreshExpandedSubtrees });
    }
    selection.clear();
  }

  function confirmBulkDelete() {
    if (!bulkDeletePending) return;
    const ids = bulkDeletePending.map((t) => t.id);
    bulkDeleteTasks(ids, { onSuccess: refreshExpandedSubtrees });
    setExpandedIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const id of ids) if (next.delete(id)) changed = true;
      return changed ? next : prev;
    });
    setChildrenByParent((prev) => {
      const rest: Record<string, TaskData[]> = {};
      for (const [pid, kids] of Object.entries(prev)) {
        if (ids.includes(pid)) continue;
        rest[pid] = kids.filter((c) => !ids.includes(c.id));
      }
      return rest;
    });
    selection.clear();
    setBulkDeletePending(null);
  }

  return (
    <AppShell>
      <div className="flex flex-col min-h-dvh">
      <div className="flex items-center gap-2 px-5 md:px-7 py-6 md:py-8 min-w-0">
        <Hamburger />
        <h2 className="flex items-center gap-2 text-xl md:text-2xl font-light text-gray-900 dark:text-white flex-1 min-w-0">
          <span className="truncate">{list.name}</span>
          <Link
            href={`/zen?list_id=${list.id}`}
            aria-label="Дзен-режим"
            title="Дзен-режим"
            className="ml-2 text-gray-400 hover:text-blue-500 dark:text-gray-500 flex-shrink-0"
          >
            <LotusIcon className="w-5 h-5 md:w-6 md:h-6" />
          </Link>
          <AiActionMenu
            entries={collectionEntries}
            ariaLabel="ИИ: навести порядок"
            buttonClassName="ml-2 text-gray-400 hover:text-blue-500 dark:text-gray-500 cursor-pointer disabled:cursor-default disabled:opacity-60 flex-shrink-0"
            iconClassName="w-5 h-5 md:w-6 md:h-6"
            spinnerClassName="block w-5 h-5 md:w-6 md:h-6 rounded-full border-2 border-current border-t-transparent animate-spin"
            testId="collection-ai-menu"
          />
        </h2>
        <div className="ml-auto flex items-center gap-1 flex-shrink-0">
          <SearchLink from={list.kind} />
        </div>
      </div>

      <div className="px-5 md:px-7 flex-1 flex flex-col">
        <form onSubmit={submitNewTask}>
          <NewTaskCard
            ref={newTaskInputRef}
            value={newTitle}
            onChange={setNewTitle}
            ariaLabel="Новая задача"
            schedule={{
              dueOn: newDueOn,
              onDueChange: setNewDueOn,
              recurrence: newRecurrence,
              onRecurrenceChange: setNewRecurrence,
            }}
          />
        </form>


        {visiblePending.length > 0 && (
          <SortableContext
            items={orderedPending.map((t) => t.id)}
            strategy={verticalListSortingStrategy}
          >
            <div
              className="-mx-2 md:-mx-4 mt-1.5 space-y-1.5"
              onClick={(e) => {
                if (e.target === e.currentTarget) selection.clear();
              }}
            >
              {visiblePending.map((task) => renderTaskTree(task, 0, true))}
            </div>
          </SortableContext>
        )}

        {visiblePending.length === 0 && (
          <EmptyState
            icon={TASKS_ICON}
            text={emptyText(list.kind, allDone)}
          />
        )}

        {completedTasksExist && (
          <div className={orderedPending.length === 0 ? "mt-auto pb-6" : "mt-3"}>
            <button
              type="button"
              onClick={completed.toggle}
              className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer flex items-center gap-1"
            >
              <svg
                className={`w-3 h-3 transition-transform ${completed.open ? "rotate-90" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              {completed.loading ? "Загрузка…" : "Завершённые"}
            </button>
            {completed.open && completed.cached && completed.cached.length > 0 && (
              <div className="-mx-2 md:-mx-4 mt-2 space-y-1.5 opacity-80">
                {completed.cached.map((task) => renderTaskTree(task, 0, false))}
              </div>
            )}
            {completed.open && completed.canLoadOlder && (
              <button
                type="button"
                onClick={completed.loadOlder}
                disabled={completed.loadingOlder}
                className="mt-2 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer disabled:cursor-default"
              >
                {completed.loadingOlder ? "Загрузка…" : "Показать более ранние"}
              </button>
            )}
          </div>
        )}
      </div>
      </div>

      {taskPendingDelete && (
        <DeleteConfirm
          title={`Удалить «${taskPendingDelete.title}»?`}
          body="Задача будет удалена безвозвратно."
          onConfirm={confirmDeleteTask}
          onCancel={() => setTaskPendingDelete(null)}
        />
      )}

      {selectedTask && (
        <DetailsPanel
          task={selectedTask}
          lists={listOptions}
          onClose={() => {
            selectTask(null);
          }}
          onDelete={(t) => {
            selectTask(null);
            setTaskPendingDelete(t);
          }}
          onTreeMutated={refreshExpandedSubtrees}
        />
      )}

      {bulkDeletePending && (
        <DeleteConfirm
          title={
            bulkDeletePending.length === 1
              ? `Удалить «${bulkDeletePending[0].title}»?`
              : `Удалить ${taskCount(bulkDeletePending.length)}?`
          }
          body={
            bulkDeletePending.length === 1
              ? "Задача будет удалена безвозвратно."
              : "Задачи будут удалены безвозвратно."
          }
          onConfirm={confirmBulkDelete}
          onCancel={() => setBulkDeletePending(null)}
        />
      )}

      {contextMenu && (
        <TaskContextMenu
          tasks={contextMenu.tasks}
          lists={listOptions}
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onPatch={applyMenuPatch}
          onDelete={() => setBulkDeletePending(contextMenu.tasks)}
        />
      )}
    </AppShell>
  );
}
